/**
 * useSignupValidation — field-level validation for the Signup form.
 *
 * Keeps the SignupPage lean by owning the validation rules + error state.
 * Returns:
 *   errors        — current error map { fieldName: message }
 *   validate(form, avatarPreview) — runs all rules, sets errors, returns boolean
 *   clearError(field) — clear a single field error (call on keystroke)
 */
import { useState, useCallback } from 'react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UPPER_RE = /[A-Z]/;
const DIGIT_RE = /[0-9]/;
// eslint-disable-next-line no-useless-escape
const SPECIAL_RE = /[!@#$%^&*()_+\-=[\]{};':"|,.<>/?]/;

export default function useSignupValidation() {
  const [errors, setErrors] = useState({});

  const validate = useCallback((form, avatarPreview) => {
    const e = {};

    if (!form.nombre.trim()) e.nombre = 'Nombre requerido';
    else if (form.nombre.trim().length < 2) e.nombre = 'Mínimo 2 caracteres';

    if (!form.apellido.trim()) e.apellido = 'Apellido requerido';
    else if (form.apellido.trim().length < 2) e.apellido = 'Mínimo 2 caracteres';

    if (!form.cedulaNum.trim()) e.cedulaNum = 'Cédula requerida';
    else if (!/^\d+$/.test(form.cedulaNum)) e.cedulaNum = 'Solo números';

    if (!form.telefonoNum.trim()) e.telefonoNum = 'Teléfono requerido';
    else if (!/^\d{7}$/.test(form.telefonoNum)) e.telefonoNum = 'Debe tener 7 dígitos';

    if (!avatarPreview) e.avatar = 'Foto de perfil requerida';

    if (!form.email.trim()) e.email = 'Correo requerido';
    else if (!EMAIL_RE.test(form.email)) e.email = 'Correo inválido';

    if (!form.password.trim()) e.password = 'Contraseña requerida';
    else if (form.password.length < 8) e.password = 'Mínimo 8 caracteres';
    else if (!UPPER_RE.test(form.password)) e.password = 'Debe incluir una mayúscula';
    else if (!DIGIT_RE.test(form.password)) e.password = 'Debe incluir un número';
    else if (!SPECIAL_RE.test(form.password)) e.password = 'Debe incluir un carácter especial (!@#$%...)';

    if (form.password !== form.confirmPassword) e.confirmPassword = 'Las contraseñas no coinciden';

    setErrors(e);
    return Object.keys(e).length === 0;
  }, []);

  const clearError = useCallback((field) => {
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }, []);

  return { errors, validate, clearError };
}

// Exposed for re-use in the strength meter
export { UPPER_RE, DIGIT_RE, SPECIAL_RE };
